<template>
    <v-container class="mt-10 pb-12" style="min-width: 1100px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">

        <!-- Header -->
        <div class="d-flex align-center mb-6">
            <v-btn icon class="mr-2" :to="{ name: 'TimeTracking' }"><v-icon>mdi-arrow-left</v-icon></v-btn>
            <div>
                <h1 class="text-h4 font-weight-bold mb-1">Unusual Activity</h1>
                <p class="text-subtitle-1 grey--text mb-0">
                    Keystroke and mouse patterns checked per 30-minute period ({{ timezone }})
                </p>
            </div>
            <v-spacer />
            <v-btn-toggle v-model="preset" mandatory dense class="mr-3" @change="applyPreset">
                <v-btn value="day" class="text-none">Day</v-btn>
                <v-btn value="week" class="text-none">Week</v-btn>
                <v-btn value="month" class="text-none">Month</v-btn>
            </v-btn-toggle>
            <v-btn icon @click="shift(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <span class="font-weight-medium mx-1 text-no-wrap">{{ rangeLabel }}</span>
            <v-btn icon :disabled="isCurrentPeriod" @click="shift(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
            <v-btn color="primary" outlined :loading="loading" class="text-none ml-3" @click="fetchData">
                <v-icon left>mdi-refresh</v-icon>Refresh
            </v-btn>
        </div>

        <v-alert v-if="error" type="error" outlined dense>{{ error }}</v-alert>

        <v-row>
            <!-- Users -->
            <v-col cols="4">
                <v-card outlined>
                    <v-card-title class="text-subtitle-2 grey--text py-2 d-flex">
                        Users
                        <v-spacer />
                        <v-switch v-model="flaggedOnly" label="Flagged only" dense hide-details class="mt-0 pt-0" />
                    </v-card-title>
                    <v-divider />
                    <v-progress-linear v-if="loading" indeterminate />
                    <v-list dense class="py-0" style="max-height: 70vh; overflow-y: auto;">
                        <v-list-item v-for="u in listedUsers" :key="u.id"
                            :input-value="selectedId === u.id" color="primary" @click="selectedId = u.id">
                            <v-list-item-avatar size="28" :color="u.unusual_seconds ? 'red lighten-4' : 'grey lighten-3'">
                                <span class="text-caption">{{ initials(u.name) }}</span>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ u.name }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ formatDuration(u.unusual_seconds) }} unusual of {{ formatDuration(u.active_seconds) }} active
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-chip x-small :color="chipColor(u.unusual_percent)" dark>{{ u.unusual_percent }}%</v-chip>
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item v-if="!loading && !listedUsers.length">
                            <v-list-item-content class="grey--text text-body-2">
                                {{ flaggedOnly ? 'No unusual activity in this period.' : 'No tracked time in this period.' }}
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>

            <!-- Detail -->
            <v-col cols="8">
                <v-card v-if="selected" outlined>
                    <v-card-title class="pb-1">
                        <v-avatar size="36" color="red lighten-4" class="mr-3">
                            <span class="text-caption">{{ initials(selected.name) }}</span>
                        </v-avatar>
                        <div>
                            <div class="text-h6">{{ selected.name }}</div>
                            <div class="text-caption grey--text">
                                Unusual Activity Total Time: {{ formatDuration(selected.unusual_seconds) }}
                                ({{ selected.unusual_percent }}% of {{ formatDuration(selected.active_seconds) }} active)
                            </div>
                        </div>
                    </v-card-title>
                    <v-divider class="mt-3" />

                    <div v-if="!selected.types.length" class="pa-6 grey--text text-center">
                        No unusual activity detected for this user in this period.
                    </div>

                    <template v-else>
                        <div class="d-flex px-4 py-2 text-caption grey--text">
                            <div style="flex: 1;">Types of unusual activity</div>
                            <div style="width: 90px;" class="text-right">Time</div>
                            <div style="width: 260px;" class="pl-6">% of active time</div>
                        </div>
                        <v-divider />
                        <v-expansion-panels accordion flat multiple>
                            <v-expansion-panel v-for="t in selected.types" :key="t.key">
                                <v-expansion-panel-header class="py-2">
                                    <div class="d-flex align-center" style="width: 100%;">
                                        <div style="flex: 1;">{{ t.label }}</div>
                                        <div style="width: 90px;" class="text-right font-weight-medium">{{ formatDuration(t.seconds) }}</div>
                                        <div style="width: 50px;" class="text-right pl-2">{{ t.percent }}%</div>
                                        <div style="width: 210px;" class="pl-3 pr-4">
                                            <v-progress-linear :value="t.percent" color="red" background-color="grey lighten-3" height="10" rounded />
                                        </div>
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-simple-table dense>
                                        <tbody>
                                            <tr v-for="p in t.periods" :key="p.start">
                                                <td class="text-no-wrap" style="width: 130px;">{{ formatDay(p.start) }}</td>
                                                <td class="text-no-wrap" style="width: 120px;">{{ p.label }}</td>
                                                <td class="text-no-wrap" style="width: 80px;">{{ formatDuration(p.seconds) }}</td>
                                                <td class="grey--text text--darken-1">{{ p.evidence }}</td>
                                            </tr>
                                        </tbody>
                                    </v-simple-table>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </template>

                    <v-divider />
                    <div class="pa-3 text-caption grey--text">
                        A flag is a prompt to look closer (e.g. screenshots for those periods on the Time Tracking page),
                        not proof of misconduct. Data entry or reading can trip some checks.
                    </div>
                </v-card>
                <v-card v-else outlined class="pa-10 text-center grey--text">
                    Select a user to see their unusual activity.
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
function ymd(d) {
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default {
    name: 'UnusualActivityComponent',

    data() {
        return {
            loading: false,
            error: null,
            users: [],
            selectedId: null,
            flaggedOnly: true,
            preset: 'day',
            anchor: new Date(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Dublin',
        };
    },

    computed: {
        range() {
            const a = new Date(this.anchor.getFullYear(), this.anchor.getMonth(), this.anchor.getDate());
            if (this.preset === 'week') {
                const from = new Date(a);
                from.setDate(a.getDate() - ((a.getDay() + 6) % 7)); // Monday
                const to = new Date(from);
                to.setDate(from.getDate() + 6);
                return { from, to };
            }
            if (this.preset === 'month') {
                return { from: new Date(a.getFullYear(), a.getMonth(), 1), to: new Date(a.getFullYear(), a.getMonth() + 1, 0) };
            }
            return { from: a, to: a };
        },
        rangeLabel() {
            const opts = { day: 'numeric', month: 'short', year: 'numeric' };
            if (this.preset === 'month') return this.range.from.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
            if (this.preset === 'day') return this.range.from.toLocaleDateString(undefined, { weekday: 'short', ...opts });
            return `${this.range.from.toLocaleDateString(undefined, opts)} – ${this.range.to.toLocaleDateString(undefined, opts)}`;
        },
        isCurrentPeriod() {
            return ymd(this.range.to) >= ymd(new Date());
        },
        listedUsers() {
            return this.flaggedOnly ? this.users.filter(u => u.unusual_seconds > 0) : this.users;
        },
        selected() {
            return this.users.find(u => u.id === this.selectedId) || null;
        },
    },

    mounted() {
        this.fetchData();
    },

    methods: {
        async fetchData() {
            this.loading = true;
            this.error = null;
            try {
                const res = await this.$axios.get('api/agent/unusual-activity', {
                    date_from: ymd(this.range.from),
                    date_to: ymd(this.range.to),
                    timezone: this.timezone,
                });
                if (res.data?.response) {
                    this.users = res.data.users || [];
                    if (!this.selected) {
                        this.selectedId = this.listedUsers.length ? this.listedUsers[0].id : null;
                    }
                } else {
                    this.users = [];
                    this.error = res.data?.message || 'Failed to load unusual activity';
                }
            } finally {
                this.loading = false;
            }
        },
        applyPreset() {
            this.anchor = new Date();
            this.fetchData();
        },
        shift(dir) {
            const a = new Date(this.anchor);
            if (this.preset === 'day') a.setDate(a.getDate() + dir);
            else if (this.preset === 'week') a.setDate(a.getDate() + 7 * dir);
            else a.setMonth(a.getMonth() + dir, 1);
            this.anchor = a;
            this.fetchData();
        },
        formatDuration(seconds) {
            const s = Math.max(0, Math.round(seconds || 0));
            const h = Math.floor(s / 3600);
            const m = Math.floor((s % 3600) / 60);
            return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
        },
        formatDay(start) {
            const [d] = start.split(' ');
            const [y, mo, da] = d.split('-').map(Number);
            return new Date(y, mo - 1, da).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
        },
        initials(name) {
            return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
        },
        chipColor(pct) {
            if (pct >= 25) return 'red';
            if (pct >= 5) return 'orange';
            return 'grey';
        },
    },
};
</script>
